import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Shield, Archive, Trash2, ArrowLeft } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1557264337-e8a93017fe92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxkZWVwJTIwc3BhY2UlMjBkYXJrfGVufDB8fHx8MTc3NzU5ODA5Mnww&ixlib=rb-4.1.0&q=85";

export default function CampaignArchive() {
  const { user } = useAuth();
  const [camps, setCamps] = useState([]);

  const load = async () => {
    try { const r = await api.get("/campaigns"); setCamps(r.data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin";
  const ended = camps.filter(c => c.ended);
  const visible = isAdmin ? ended : ended.filter(c => c.gm_id === user?.id || (c.party_ids || []).includes(user?.id));

  const remove = async (e, id, name) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`DELETE archived campaign "${name}"?\n\nThis permanently erases all sessions, chat history, and custom questions. Characters will be unassigned.\n\nThis CANNOT be undone.`)) return;
    if (!window.confirm(`Final confirmation: really delete "${name}"?`)) return;
    try { await api.delete(`/campaigns/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <Archive size={22} strokeWidth={1.5} className="amber" />
            <h1 className="heading-mono text-3xl sm:text-4xl amber amber-glow uppercase tracking-tight">// Campaign Archive</h1>
          </div>
          <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-[0.2em] uppercase">
            Ended operations — read-only history
          </div>
        </div>
        <Link to="/campaigns" className="btn-amber flex items-center gap-2" data-testid="back-to-campaigns">
          <ArrowLeft size={14} strokeWidth={1.5} /> Active
        </Link>
      </div>

      {visible.length === 0 ? (
        <div className="panel p-12 text-center" data-testid="archive-empty">
          <Archive size={42} strokeWidth={1} className="amber opacity-40 mx-auto mb-3" />
          <div className="mono text-[#A0A0A0]">// No archived campaigns yet.</div>
          <div className="mono text-[10px] text-[#A0A0A0] mt-1">Campaigns marked as ended will appear here.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="archive-grid">
          {visible.map(c => (
            <Link key={c.id} to={`/campaigns/${c.id}`} data-testid={`archived-${c.id}`} className="panel p-0 overflow-hidden hover:shadow-[0_0_18px_rgba(255,176,0,0.2)] transition-all relative opacity-80 hover:opacity-100">
              <div className="h-32 bg-cover bg-center opacity-50 relative" style={{ backgroundImage: `url(${c.image_path ? fileUrl(c.image_path) : BG})`, filter: "grayscale(0.8) hue-rotate(-15deg)" }}>
                {c.image_path && (
                  <div className="absolute bottom-2 right-2 w-16 h-16 rounded-full overflow-hidden border-2 border-[#FFB000] opacity-80">
                    <img src={fileUrl(c.image_path)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="absolute top-2 left-2 mono text-[10px] bg-[#FF2A2A] text-black px-2 py-0.5 tracking-widest">CAMPAIGN ENDED</div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <Shield size={14} strokeWidth={1.5} className="amber" />
                  <div className="heading-mono text-lg amber uppercase flex-1 truncate">{c.name}</div>
                  {isAdmin && (
                    <button onClick={(e)=>remove(e, c.id, c.name)} className="text-[#FF2A2A] p-1 hover:bg-[rgba(255,42,42,0.1)]" data-testid={`delete-archived-${c.id}`} title="Delete (admin only)">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="mono text-xs text-[#A0A0A0] mt-1">{c.game_type}</div>
                <div className="flex items-center justify-between mt-3 mono text-[10px]">
                  <span className="text-[#A0A0A0]">Party: {c.party_ids?.length || 0}</span>
                  {c.ended_at && <span className="text-[#A0A0A0]">ended {new Date(c.ended_at).toLocaleDateString()}</span>}
                  {c.gm_id === user?.id && <span className="amber">GM</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
